// Checkbox — 44×44 hit area, 28×28 visual square, sage tick when
// checked. Used across Prerequisites screens and on every task
// rectangle.
//
// Presentational and uncontrolled-friendly: the consumer owns state and
// passes `checked` plus `onChange`. The accessible `label` is required —
// every checkbox in CookChow has a visible neighbour or list-row label.
//
// See README §6.3 (a roomy checkbox on the left, sage tick when
// checked), §8 (44×44 touch target, 28×28 visual).

import { theme } from '../theme';

export type CheckboxState = 'unchecked' | 'checked' | 'disabled';

export interface CheckboxProps {
  state: CheckboxState;
  label: string;
  onChange?: (next: boolean) => void;
}

export function Checkbox({ state, label, onChange }: CheckboxProps) {
  const checked = state === 'checked';
  const disabled = state === 'disabled';
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onChange?.(!checked);
      }}
      style={{
        width: '44px',
        height: '44px',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '6px',
          background: checked ? theme.accent.calm : theme.bg.base,
          border: `2px solid ${checked ? theme.accent.calm : theme.text.muted}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Per README §7.2: 200–300 ms ease-out, no bounce. Reduced
          // motion zeroes this transition out — see styles.css.
          transition: `background ${theme.motion.duration.fast} ${theme.motion.easing}, border-color ${theme.motion.duration.fast} ${theme.motion.easing}`,
        }}
        className="cc-checkbox-box"
      >
        {checked && (
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke={theme.text.primary}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="5 12 10 17 19 7" />
          </svg>
        )}
      </span>
    </button>
  );
}

export default Checkbox;
